import gamePreviewIcon from '../../assets/icons/preview-icon.svg';
import optionsYellowIcon from '../../assets/icons/options-yellow-icon.svg';
import defaultGameIcon1 from '../../assets/icons/default-game-icon-1.svg';
import draftsEditIcon from '../../assets/icons/drafts-edit-icon.svg';
import draftsDeleteIcon from '../../assets/icons/drafts-delete-icon.svg';
import './index.scss';
import { useEffect, useState } from 'react';
import { useAppDispatcher, useAppSelector } from '../../hooks/redux';
import { LoadingIndicator } from '../Loading';
import { NoDataAvailable } from '../NoDataAvailable';
import { deleteDraft, getDraftList } from '../../api/draft';
import { IAPIResponse } from '../../interfaces/api';
import { IGameDraftDeleteResponse, IGameSendForApprovalResponse, IGameInfo, GameOperation, IndexedGameInfo } from '../../interfaces/game-info';
import { addDraftedGames } from '../../slices/drafts';
import { ConfirmationPopupWithReason, NewGamePopup, NotificationPopup } from '../Popup';
import { AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { sendGameForApproval } from '../../api/makerChecker';
import { faUpload } from '@fortawesome/pro-duotone-svg-icons';
import { useRefreshEventSubscriber } from '../../hooks/rxjs';
import { Tooltip } from "@progress/kendo-react-tooltip";
import { checkAPIResponse } from '../../utils/utils';
import { log } from '../../utils/logger';

export const DraftsPanel = () => {
    const dispatcher = useAppDispatcher();
    const lanid: string = useAppSelector((state) => state.userInfo.lanid);
    const draftsGameInfo: IndexedGameInfo = useAppSelector((state) => state.draftedGames.info);
    const [isDraftGamesListLoading, setDraftGamesListLoadingStatus] = useState<boolean>(false);
    const [isDraftOnEdit, setDraftOnEdit] = useState<{ isVisible: boolean, operation: GameOperation, gameDetails: IGameInfo | null }>({ isVisible: false, operation: 'update', gameDetails: null });
    const [confirmationPopupVisibility, setConfirmationPopupVisibility] = useState<{ visibility: boolean, title: string, message: string, isReasonRequired: boolean, onConfirmation: Function, onCancellation: Function }>({ visibility: false, title: '', message: '', isReasonRequired: false, onConfirmation: () => { }, onCancellation: () => { } });
    const [errorPopupVisibility, setErrorPopupVisibility] = useState<{ visibility: boolean, message: string }>({ visibility: false, message: '' });

    const getDraftedGames = async (): Promise<void> => {
        setDraftGamesListLoadingStatus(true);
        const draftList: IAPIResponse<Array<IGameInfo>> = await getDraftList<IGameInfo>(lanid);
        const formattedResponse = checkAPIResponse<Array<IGameInfo>>(draftList);

        if (formattedResponse.status === true && draftList.response != null && draftList.response.data != null) {
            dispatcher(addDraftedGames(draftList.response.data));
        }
        else {
            if (formattedResponse.message != null) {
                log.Error(`Unable to load drafts (${formattedResponse.message})`);
            }
            else {
                log.Error(`Unable to load drafts`);
            }
        }

        setDraftGamesListLoadingStatus(false);
    }

    const onNewGameClosePopup = (): void => {
        setDraftOnEdit({ isVisible: false, operation: 'update', gameDetails: null });
    }

    const deleteGame = async (gameName: string): Promise<void> => {
        hideConfirmationPopup();

        if (gameName != null && gameName !== "") {
            //Delete the game using the api
            const response: IAPIResponse<IGameDraftDeleteResponse> = await deleteDraft(lanid, gameName);
            const formattedResponse = checkAPIResponse<IGameDraftDeleteResponse>(response);

            if (formattedResponse.status === false) {
                setErrorPopupVisibility({ visibility: true, message: `Unable to delete draft ${gameName} (${formattedResponse.message})` });
            }
        }
    }

    const sendForApproval = async (gameName: string, operation: string | null, reason: string): Promise<void> => {
        hideConfirmationPopup();

        if (gameName != null && gameName !== "" && operation != null && operation !== "") {

            const response: IAPIResponse<IGameSendForApprovalResponse> = await sendGameForApproval(lanid, gameName, operation, reason);
            const formattedResponse = checkAPIResponse<IGameSendForApprovalResponse>(response);

            if (formattedResponse.status === false) {
                setErrorPopupVisibility({ visibility: true, message: `Unable to send game ${gameName} for approval (${formattedResponse.message})` });
            }
        }
    }

    const hideConfirmationPopup = (): void => {
        setConfirmationPopupVisibility({ visibility: false, message: '', title: '', isReasonRequired: false, onCancellation: () => { }, onConfirmation: () => { } });
    }

    useEffect(() => {
        getDraftedGames();
    }, []);

    useRefreshEventSubscriber('draft', getDraftedGames);

    return <div className='drafts-list-wrapper'>
        <div className='drafts-header-container'>
            <div className='drafts-header-wrapper'>
                <img src={gamePreviewIcon} alt='' />
                <span>Drafts</span>
            </div>
            <div className='drafts-header-options-container'>
                {/* <img src={optionsYellowIcon} alt='' /> */}
            </div>
        </div>
        <div className='drafts-body-container'>
            {isDraftGamesListLoading === true ? <LoadingIndicator /> : (Object.values(draftsGameInfo).length > 0 ?
                <>
                    {Object.values(draftsGameInfo).map((obj: IGameInfo, index: number) => (
                        <div className='game-info-container maker-checker' key={`draft-container-${index}`}>
                            <div className='game-info-image-container'>
                                <img src={defaultGameIcon1} alt='' />
                            </div>
                            <div className='game-info-details-container'>
                                <span className='game-name-text'>{obj.gameName}</span>
                                <span className='game-status-text'>{`Type: ${obj.status}`}</span>
                            </div>
                            <div className='game-info-edit-options-container'>
                                <Tooltip anchorElement="target" className='tooltip-custom'>
                                    <img src={draftsEditIcon} alt='' title={'Edit Draft'} onClick={() => setDraftOnEdit({ isVisible: true, operation: obj.operation.toLowerCase() === 'create' ? 'update' : obj.operation, gameDetails: obj })} />
                                    <img src={draftsDeleteIcon} alt='' title={'Delete Draft'} onClick={() => setConfirmationPopupVisibility({
                                        title: 'Confirm to delete',
                                        message: `Are you sure you want to delete game ${obj.gameName}`,
                                        isReasonRequired: false,
                                        onCancellation: hideConfirmationPopup,
                                        onConfirmation: () => deleteGame(obj.gameName),
                                        visibility: true,
                                    })} />
                                    <FontAwesomeIcon icon={faUpload} color={"#eda531"} title={'Send for Approval'} onClick={() => setConfirmationPopupVisibility({
                                        title: 'Confirm to send for approval',
                                        message: `Are you sure you want to send this game for approval?`,
                                        isReasonRequired: true,
                                        onCancellation: hideConfirmationPopup,
                                        onConfirmation: (reason: string) => sendForApproval(obj.gameName, obj.operation, reason),
                                        visibility: true,
                                    })} />
                                </Tooltip>
                            </div>
                        </div>
                    ))
                    }
                </>
                : <NoDataAvailable />)}
        </div>
        <AnimatePresence>
            {isDraftOnEdit.isVisible === true &&
                <NewGamePopup
                    isApprovalMode={false}
                    updationGameInfo={isDraftOnEdit.gameDetails}
                    closePopup={onNewGameClosePopup}
                />
            }
            {confirmationPopupVisibility.visibility === true &&
                <ConfirmationPopupWithReason
                    title={confirmationPopupVisibility.title}
                    message={confirmationPopupVisibility.message}
                    onConfirmation={confirmationPopupVisibility.onConfirmation}
                    onCancellation={confirmationPopupVisibility.onCancellation}
                    isModifyReasonRequired={confirmationPopupVisibility.isReasonRequired}
                />
            }
            {errorPopupVisibility.visibility === true &&
                <NotificationPopup
                    message={errorPopupVisibility.message}
                    closePopup={() => setErrorPopupVisibility({ visibility: false, message: '' })}
                />
            }
        </AnimatePresence>
    </div>
}